/**
 * Cognito User Pool password policy mirror.
 *
 * The rules below mirror the `password_policy` block in the Terraform repo
 * `sr-aws-workload` (DevOps-owned). Keep this file and Terraform in sync —
 * any change to the pool policy requires updating both.
 */

export const PASSWORD_SYMBOLS = new Set(`^$*.[]{}()?"!@#%&/\\,><':;|_~\`=+- `);

export const containsCharMatching = (
  value: string,
  predicate: (char: string) => boolean,
): boolean => [...value].some(predicate);

export interface PasswordRule {
  id: string;
  label: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: 'Password must be at least 8 characters',
    test: (password) => password.length >= 8,
  },
  {
    id: 'number',
    label: 'Use a number',
    test: (password) =>
      containsCharMatching(password, (char) => char >= '0' && char <= '9'),
  },
  {
    id: 'lowercase',
    label: 'Use a lowercase letter',
    test: (password) =>
      containsCharMatching(password, (char) => char >= 'a' && char <= 'z'),
  },
  {
    id: 'uppercase',
    label: 'Use an uppercase letter',
    test: (password) =>
      containsCharMatching(password, (char) => char >= 'A' && char <= 'Z'),
  },
  {
    id: 'symbol',
    label: 'Use a special character',
    test: (password) =>
      containsCharMatching(password, (char) => PASSWORD_SYMBOLS.has(char)),
  },
];

export const allRulesPass = (password: string): boolean =>
  PASSWORD_RULES.every((rule) => rule.test(password));

export const passwordChecksFor = (
  password: string,
): Array<PasswordRule & { met: boolean }> =>
  PASSWORD_RULES.map((rule) => ({ ...rule, met: rule.test(password) }));
